import { useEffect, useId, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';

import {
    setNextPreviewsInfo,
} from '../redux/actions/dataActions';

import { CHAT_THREAD_PREVIEW_INFO_PROPS } from '../constants/preview_info_constants';
import { loadTrack } from './audioHelper';
import PreviewInfo from './PreviewInfo';
import ChatLoadingExperience from './ChatLoadingExperience';
import SpeakerRow from './SpeakerRow';
import ShowRow from './ShowRow';
import TrackInfoRow from './TrackInfoRow';
import {
    CHAT_V2_EXPLORE_NEXT_FALLBACK_EMOJI,
    dedupeChatV2ExploreNextSuggestions,
    normalizeChatV2TopicKey,
    resolveChatV2ExploreNextSuggestions,
} from '../utils/chatV2TopicUtils';
import {
    normalizeChatV2Progress,
} from '../utils/chatV2ProgressUtils';
import { getChatPreviewHeading } from '../utils/chatPreviewPresentation';
import { chatResponsePreviews, isPlayableChatPreview } from '../utils/chatPreviewQueue';
import { parseChatSourceLinks } from '../utils/chatSourceLinks';
import { chatRequestFailed, chatRequestResponseText } from '../utils/chatRequestFailure';
import ChatRequestFailure from './ChatRequestFailure';
import AssistantTextBubble from './AssistantTextBubble';

const CHAT_V2_EXPLORE_NEXT_VISIBLE_COUNT = 3;

const presentationText = (value) => typeof value === 'string' ? value.trim() : '';

const renderChatText = (text) => parseChatSourceLinks(text).map((part, index) => part.href
    ? <a key={index} href={part.href} target='_blank' rel='noopener noreferrer'>{part.text}</a>
    : part.text);

const renderRecommendationCard = ({ component, index, playPreview, playTrack, previewPresentationMode }) => {
    if (!component || !component.type) {
        return null;
    }

    const key = component.id || `${component.type}-${index}`;

    if (component.type === 'text') {
        if (previewPresentationMode === 'summary_only' && component.id === 'closing_summary') return null;
        const content = presentationText(component.payload?.content);
        return content ? <AssistantTextBubble key={key} className='chatV2LeadText'>{renderChatText(content)}</AssistantTextBubble> : null;
    }

    if (component.type === 'speaker') {
        return (
            <div key={key} className='chatV2RecommendationItem'>
                <ChatV2SpeakerCard component={component} />
            </div>
        );
    }

    if (component.type === 'preview') {
        const payload = component.payload || {};
        const summaryOnly = previewPresentationMode === 'summary_only';
        const heading = summaryOnly ? getChatPreviewHeading(component.presentation?.heading) : null;
        if (!isPlayableChatPreview(payload)) {
            return <div key={key} className='chatV2RecommendationItem chatV2LeadText'>This recommendation is unavailable.</div>;
        }
        const playAnswerPreview = playPreview(payload);

        return (
            <div key={key} className='chatV2RecommendationItem'>
                <div className='chatV2RecommendationCardShell chatV2PreviewCardShell'>
                    <PreviewInfo
                        preview={payload}
                        generatedHeading={heading || undefined}
                        previewPresentationMode={previewPresentationMode}
                        index={index}
                        indexInViewport={() => {}}
                        playPreview={playAnswerPreview}
                        onTopicPlay={playAnswerPreview}
                        topicActionLabel='Play preview from your answer'
                        {...CHAT_THREAD_PREVIEW_INFO_PROPS}
                    />
                </div>
            </div>
        );
    }

    if (component.type === 'episode') {
        return (
            <div key={key} className='chatV2RecommendationItem'>
                <div className='chatV2RecommendationCardShell chatV2EpisodeCardShell'>
                    <TrackInfoRow
                        trackInfo={component.payload || {}}
                        playTrack={playTrack}
                        containerClassName='chatV2EpisodeTrackRow'
                        relativePublishedDate
                    />
                </div>
            </div>
        );
    }

    if (component.type === 'podcast') {
        return (
            <div key={key} className='chatV2RecommendationItem'>
                <ChatV2PodcastCard component={component} />
            </div>
        );
    }

    return null;
};

const renderRecommendationSection = ({ section, sectionIndex, playPreview, playTrack, previewPresentationMode }) => {
    const sectionComponents = Array.isArray(section && section.components) ? section.components : [];
    const renderedItems = sectionComponents.map((component, itemIndex) => {
        const element = renderRecommendationCard({
            component,
            index: itemIndex,
            playPreview,
            playTrack,
            previewPresentationMode,
        });

        if (!element) {
            return null;
        }

        return {
            key: component.id || `${component.type || 'item'}-${itemIndex}`,
            type: component.type,
            isPreviewCard: component.type === 'preview' && isPlayableChatPreview(component.payload),
            element,
        };
    }).filter(Boolean);

    if (!section || renderedItems.length === 0) {
        return null;
    }

    const repeatsPreviewTitle = previewPresentationMode === 'summary_only' && sectionComponents.some((component) => (
        component.type === 'preview' &&
        presentationText(component.payload?.title).toLowerCase() === presentationText(section.sectionIntroText).toLowerCase()
    ));
    const previewOnly = renderedItems.every((item) => item.type === 'preview');
    const introText = repeatsPreviewTitle ? '' : section.sectionIntroText;
    const startsWithPreview = !presentationText(introText) && renderedItems[0].isPreviewCard;
    const endsWithPreview = renderedItems[renderedItems.length - 1].isPreviewCard;

    return (
        <div key={section.sectionId || `section-${sectionIndex}`}
            className={[
                'chatV2Bubble chatV2SectionBubble chatV2RecommendationSection',
                previewOnly && 'chatV2PreviewSection',
                startsWithPreview && 'chatV2StartsWithPreview',
                endsWithPreview && 'chatV2EndsWithPreview',
            ].filter(Boolean).join(' ')}>
            <div className='chatV2RecommendationSectionBody'>
                <ChatV2SectionIntro text={introText} />
                {renderedItems.map((item, itemIndex) => (
                    <div
                        key={`${item.key}-wrapper`}
                        className={itemIndex > 0 ? [
                            'chatV2RecommendationItemDivider',
                            item.isPreviewCard && renderedItems[itemIndex - 1].isPreviewCard ? 'chatV2AdjacentPreview' : '',
                        ].filter(Boolean).join(' ') : undefined}
                    >
                        {item.element}
                    </div>
                ))}
            </div>
        </div>
    );
};

const ChatV2TextBlock = ({ text }) => {
    const content = typeof text === 'string' ? text.trim() : '';

    if (!content) {
        return null;
    }

    return (
        <div className='chatV2Bubble chatV2SummaryBubble'>
            <AssistantTextBubble className='chatV2LeadText'>
                {renderChatText(content)}
            </AssistantTextBubble>
        </div>
    );
};

const ChatV2ExploreNextTopics = ({
    suggestions,
    previousSuggestions,
    exploreNextGroupKey = 'explore_next_topics',
    isTopicAlreadyExplored,
    onSelectExploreNextTopic,
    exploreNextTopicsDisabled = false,
}) => {
    const normalizedSuggestions = resolveChatV2ExploreNextSuggestions({
        exploreNextSuggestions: suggestions,
    });
    const normalizedPreviousSuggestions = resolveChatV2ExploreNextSuggestions({
        exploreNextSuggestions: previousSuggestions,
    });
    const visibleSuggestions = normalizedSuggestions.filter((suggestion) => {
        if (typeof isTopicAlreadyExplored !== 'function') {
            return true;
        }

        return !isTopicAlreadyExplored(suggestion.text);
    });
    const visiblePreviousSuggestions = normalizedPreviousSuggestions.filter((suggestion) => {
        if (typeof isTopicAlreadyExplored !== 'function') {
            return true;
        }

        return !isTopicAlreadyExplored(suggestion.text);
    });
    const dedupedVisibleSuggestions = dedupeChatV2ExploreNextSuggestions(visibleSuggestions);
    const currentTopicKeys = new Set(dedupedVisibleSuggestions.map((suggestion) => normalizeChatV2TopicKey(suggestion)));
    const dedupedVisiblePreviousSuggestions = dedupeChatV2ExploreNextSuggestions(
        visiblePreviousSuggestions.filter((suggestion) => !currentTopicKeys.has(normalizeChatV2TopicKey(suggestion)))
    );
    const allVisibleSuggestions = dedupedVisibleSuggestions.concat(dedupedVisiblePreviousSuggestions);
    const [showAllTopics, setShowAllTopics] = useState(false);
    const topicsId = useId();
    const titleRef = useRef(null);
    const shouldScrollToTitleRef = useRef(false);

    useEffect(() => {
        setShowAllTopics(false);
        shouldScrollToTitleRef.current = false;
    }, [exploreNextGroupKey]);

    useEffect(() => {
        if (showAllTopics || !shouldScrollToTitleRef.current) {
            return;
        }

        shouldScrollToTitleRef.current = false;
        requestAnimationFrame(() => {
            titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, [showAllTopics]);

    if (allVisibleSuggestions.length === 0) {
        return null;
    }

    const displayedSuggestions = showAllTopics ?
        allVisibleSuggestions :
        allVisibleSuggestions.slice(0, CHAT_V2_EXPLORE_NEXT_VISIBLE_COUNT);
    const hasMoreTopics = allVisibleSuggestions.length > CHAT_V2_EXPLORE_NEXT_VISIBLE_COUNT;
    const showPreviousTopicsMarker = showAllTopics && dedupedVisiblePreviousSuggestions.length > 0;
    const previousTopicsStartIndex = dedupedVisibleSuggestions.length;

    const toggleTopics = () => {
        setShowAllTopics((current) => {
            const next = !current;

            if (!next) {
                shouldScrollToTitleRef.current = true;
            }

            return next;
        });
    };

    const renderTopicRow = (suggestion, key) => {
        const nextTopic = suggestion.text;
        const emoji = suggestion.emoji || CHAT_V2_EXPLORE_NEXT_FALLBACK_EMOJI;

        return (
            <button
                type='button'
                key={key}
                onClick={() => {
                    if (typeof onSelectExploreNextTopic === 'function') {
                        onSelectExploreNextTopic(nextTopic);
                    }
                }}
                disabled={exploreNextTopicsDisabled || typeof onSelectExploreNextTopic !== 'function'}
                className='clampSafeButton chatV2FollowupQuestionRow'
                aria-label={`Explore ${nextTopic}`}>
                <span className='chatV2FollowupQuestionEmoji' aria-hidden='true'>{emoji}</span>
                <span className='chatV2FollowupQuestionText'>{nextTopic}</span>
            </button>
        );
    };

    const renderedTopicRows = displayedSuggestions.flatMap((suggestion, index) => {
        const rows = [];

        if (index > 0) {
            rows.push(
                <div
                    key={`${exploreNextGroupKey}-divider-${index}`}
                    className='chatV2FollowupRowDivider'
                    aria-hidden='true'
                />
            );
        }

        if (showPreviousTopicsMarker && index === previousTopicsStartIndex) {
            rows.push(
                <div key={`${exploreNextGroupKey}-previous-divider`} className='chatV2FollowupPreviousLabel'>
                    Previous
                </div>
            );
        }

        rows.push(renderTopicRow(suggestion, `${exploreNextGroupKey}-${index}`));
        return rows;
    });

    return (
        <div className='chatV2Bubble chatV2FollowupSection chatV2FollowupBubble'>
            <div className='chatV2RecommendationSectionBody'>
                <div ref={titleRef} className='chatV2FollowupLabel'>
                    <AssistantTextBubble>Explore next</AssistantTextBubble>
                </div>
                <div className='chatV2FollowupListShell'>
                    <div className='chatV2FollowupList' id={topicsId}>
                        {renderedTopicRows}
                    </div>
                    {hasMoreTopics && (
                        <div className='chatV2FollowupToggleRow'>
                            <button
                                type='button'
                                onClick={toggleTopics}
                                className='chatV2FollowupToggle'
                                aria-expanded={showAllTopics}
                                aria-controls={topicsId}
                                aria-label={showAllTopics ? 'Show fewer follow-up topics' : 'Show more follow-up topics'}
                            >
                                {showAllTopics ? <ExpandLess aria-hidden='true' /> : <ExpandMore aria-hidden='true' />}
                                <span>{showAllTopics ? 'Less' : 'More'}</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ChatV2SectionIntro = ({ text }) => {
    const content = typeof text === 'string' ? text.trim() : '';

    if (!content) {
        return null;
    }

    return (
        <AssistantTextBubble className='chatV2SectionIntro'>
            {content}
        </AssistantTextBubble>
    );
};

const ChatV2SpeakerCard = ({ component }) => {
    const navigate = useNavigate();
    const payload = component && component.payload ? component.payload : {};
    const speakerId = payload.id || '';
    const name = payload.name || 'Speaker';
    const description = payload.description || '';
    const imageURL = payload.imageURL || '';

    const openSpeaker = () => {
        if (!speakerId) {
            return;
        }

        navigate(`/speaker/${encodeURIComponent(speakerId)}`);
    };

    return (
        <div className='chatV2SpeakerCardShell'>
            <SpeakerRow speaker={{ id: speakerId, name, description, imageURL }}
                onSpeakerClick={openSpeaker} />
        </div>
    );
};

const normalizeChatV2PodcastPayload = (payload) => {
    const normalizedPayload = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
    const rawShowType = normalizedPayload && typeof normalizedPayload.showType === 'string' ?
        normalizedPayload.showType.trim().toLowerCase() :
        '';
    const explicitShow = normalizedPayload && normalizedPayload.show && typeof normalizedPayload.show === 'object' && !Array.isArray(normalizedPayload.show) ?
        normalizedPayload.show :
        null;

    if (!explicitShow || (rawShowType !== 'indexed' && rawShowType !== 'unindexed')) {
        return null;
    }

    if (rawShowType === 'indexed') {
        return {
            showType: 'indexed',
            show: {
                id: typeof explicitShow.id === 'string' ? explicitShow.id.trim() : '',
                name: typeof explicitShow.name === 'string' ? explicitShow.name.trim() : '',
                appleCollectionId: explicitShow.appleCollectionId,
                rssURL: explicitShow.rssURL,
                creator: presentationText(explicitShow.creator),
                description: typeof explicitShow.description === 'string' ? explicitShow.description.trim() : '',
                imageURL: typeof explicitShow.imageURL === 'string' ? explicitShow.imageURL.trim() : '',
            },
        };
    }

    return {
        showType: 'unindexed',
        show: {
            name: typeof explicitShow.name === 'string' ? explicitShow.name.trim() : '',
            appleCollectionId: explicitShow.appleCollectionId,
            creator: presentationText(explicitShow.creator),
            description: typeof explicitShow.description === 'string' ? explicitShow.description.trim() : '',
            imageURL: typeof explicitShow.imageURL === 'string' ? explicitShow.imageURL.trim() : '',
            rssURL: typeof explicitShow.rssURL === 'string' ? explicitShow.rssURL.trim() : '',
        },
    };
};

export const ChatV2PodcastCard = ({ component }) => {
    const navigate = useNavigate();
    const payload = component && component.payload ? component.payload : {};
    const normalizedPodcastPayload = normalizeChatV2PodcastPayload(payload);
    const normalizedShow = normalizedPodcastPayload && normalizedPodcastPayload.show ? normalizedPodcastPayload.show : {};
    const title = normalizedShow.name || 'Show';
    const creator = normalizedShow.creator || '';
    const description = normalizedShow.description || payload.introText || '';
    const imageURL = normalizedShow.imageURL || '';
    const rssURL = normalizedPodcastPayload && normalizedPodcastPayload.showType === 'unindexed' ?
        normalizedShow.rssURL || '' :
        '';
    const podcastId = normalizedPodcastPayload && normalizedPodcastPayload.showType === 'indexed' ?
        normalizedShow.id || '' :
        '';
    const hasPodcastPage = Boolean(podcastId);
    const canLoadUnknownPodcast = !hasPodcastPage && Boolean(title && rssURL);

    if (!normalizedPodcastPayload) {
        return null;
    }

    const openPodcast = () => {
        if (hasPodcastPage) {
            navigate(`/podcast/${podcastId}`);
            return;
        }

        if (canLoadUnknownPodcast) {
            navigate('/load_podcast', {
                state: {
                    title,
                    imageSrc: imageURL,
                    rssURL,
                    ...(normalizedShow.appleCollectionId ? { appleCollectionId: normalizedShow.appleCollectionId } : {}),
                },
            });
        }
    };

    return (
        <div className='chatV2PodcastCardShell'>
            <ShowRow show={{ ...normalizedShow, name: title,
                source: hasPodcastPage ? normalizedShow : undefined,
                key: podcastId ? `source:${podcastId}` : `rss:${rssURL}` }}
                description={creator || description}
                openLabel={`Open ${title}${creator ? `, ${creator}` : ''}`}
                onOpen={openPodcast} disabled={!hasPodcastPage && !canLoadUnknownPodcast} />
        </div>
    );
};

export const ChatV2Components = ({
    authenticated,
    progress,
    useServerPlanningMessages = false,
    hasEarlyShowMatch = false,
    runId,
    runStatus,
    error,
    onEditRequest,
    editRequestDisabled,
    assistantText,
    sections,
    previewPresentationMode,
    exploreNextSuggestions,
    exploreNextTopics,
    previousExploreNextSuggestions,
    previousExploreNextTopics,
    exploreNextGroupKey,
    exploreNextTopicsDisabled,
    isTopicAlreadyExplored,
    onSelectExploreNextTopic,
    setNextPreviewsInfo,
}) => {
    const navigate = useNavigate();
    const normalizedSections = Array.isArray(sections) ? sections : [];
    const normalizedExploreNextSuggestions = resolveChatV2ExploreNextSuggestions({
        exploreNextSuggestions,
        exploreNextTopics,
    });
    const normalizedPreviousExploreNextSuggestions = resolveChatV2ExploreNextSuggestions({
        exploreNextSuggestions: previousExploreNextSuggestions,
        exploreNextTopics: previousExploreNextTopics,
    });
    const normalizedProgress = normalizeChatV2Progress(progress);
    const runIsActive = runStatus
        ? runStatus === 'queued' || runStatus === 'running'
        : normalizedProgress?.state === 'queued' || normalizedProgress?.state === 'running';
    const shouldRenderLoading = runIsActive && normalizedProgress?.state !== 'failed';
    const shouldRenderFailure = chatRequestFailed({ status: runStatus, progress: normalizedProgress });

    const playPreview = (preview) => () => {
        const previews = chatResponsePreviews(normalizedSections);
        if (!previews.some(item => item.id === preview?.id)) return;
        loadTrack(preview.mp3URL, 0);
        setNextPreviewsInfo(authenticated, previews, {
            initialPreviewId: preview.id,
            relatedPreviewId: preview.id,
            playbackSource: 'chat',
            responsePreviewIds: previews.map(item => item.id),
        });
        navigate('/previews', { state: { closeAllModal: true, title: 'Highlights' } });
    };

    const playTrack = (trackInfo, startSeconds = 0) => () => {
        if (trackInfo && trackInfo.id) {
            navigate(`/track/${encodeURIComponent(trackInfo.id)}?autoplay=true&start=${startSeconds}`);
        }
    };

    return (
        <div className={`chatV2ResponseStack${previewPresentationMode === 'summary_only' ? ' chatV3ResponseStack' : ''}`}>
            {shouldRenderLoading && <ChatLoadingExperience key={runId} progress={normalizedProgress}
                useServerPlanningMessages={useServerPlanningMessages} hasEarlyShowMatch={hasEarlyShowMatch} />}
            {shouldRenderFailure && <ChatRequestFailure
                error={error || (normalizedProgress?.state === 'failed' ? normalizedProgress.message : '')}
                onEditRequest={onEditRequest} disabled={editRequestDisabled} />}
            <ChatV2TextBlock text={chatRequestResponseText({ status: runStatus, progress: normalizedProgress, error, assistantText })} />
            {normalizedSections.map((section, sectionIndex) => renderRecommendationSection({
                section,
                sectionIndex,
                playPreview,
                playTrack,
                previewPresentationMode,
            }))}
            {(normalizedExploreNextSuggestions.length > 0 || normalizedPreviousExploreNextSuggestions.length > 0) && (
                <ChatV2ExploreNextTopics
                    exploreNextGroupKey={exploreNextGroupKey}
                    suggestions={normalizedExploreNextSuggestions}
                    previousSuggestions={normalizedPreviousExploreNextSuggestions}
                    isTopicAlreadyExplored={isTopicAlreadyExplored}
                    onSelectExploreNextTopic={onSelectExploreNextTopic}
                    exploreNextTopicsDisabled={exploreNextTopicsDisabled}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
});

const mapActionsToProps = {
    setNextPreviewsInfo,
};

export default connect(mapStateToProps, mapActionsToProps)(ChatV2Components);
